import { useCallback, useEffect, useRef, useState } from 'react'

const COMMIT = /^[0-9a-f]{40}$/
const ACTIVE = new Set(['pending_verification', 'building', 'image_published', 'deploying'])
const STATE = {
  pending_verification: '요청 접수 · 빌드 대기',
  building: '이미지 빌드 중',
  image_published: '이미지 게시 확인 · 배포 대기',
  deploying: '개발 노드 반영 중',
  verified: '배포 후 준비 상태 검증 완료',
  cancelled: '요청 취소',
}

function newRequestKey() {
  const bytes = new Uint8Array(16)
  window.crypto.getRandomValues(bytes)
  bytes[6] = (bytes[6] & 15) | 64
  bytes[8] = (bytes[8] & 63) | 128
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

export default function DevelopmentRunPanel({ service, api, requestKeyFactory = newRequestKey }) {
  const [sourceCommit, setSourceCommit] = useState('')
  const [requests, setRequests] = useState([])
  const [executionConnected, setExecutionConnected] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const retry = useRef(null)
  const serviceId = service?.service_id
  const eligible = service?.dev_target?.startsWith('k8s:antsnest/') === true

  const refresh = useCallback(async () => {
    if (!serviceId || !eligible) return
    try {
      const result = await api(`/services/${encodeURIComponent(serviceId)}/runs/development`)
      setRequests(result.requests || [])
      setExecutionConnected(result.executionConnected === true)
    } catch (error) {
      setExecutionConnected(false)
      throw error
    }
  }, [api, eligible, serviceId])

  useEffect(() => {
    setSourceCommit(''); setRequests([]); setError(''); setNotice('')
    setExecutionConnected(false); retry.current = null
    if (!eligible) return undefined
    let live = true
    const poll = async () => {
      try { if (live) await refresh() }
      catch { if (live) setError('개발 배포 이력을 읽을 수 없습니다.') }
    }
    poll()
    const timer = setInterval(poll, 5000)
    return () => { live = false; clearInterval(timer) }
  }, [eligible, refresh, serviceId])

  if (!service) return null
  const active = requests.some(request => ACTIVE.has(request.state))

  async function submit(event) {
    event.preventDefault()
    if (!eligible || !executionConnected || !COMMIT.test(sourceCommit) || active || busy) return
    setBusy(true); setError(''); setNotice('')
    try {
      const attempt = retry.current?.sourceCommit === sourceCommit ? retry.current
        : { sourceCommit, requestKey: requestKeyFactory() }
      retry.current = attempt
      const result = await api(`/services/${encodeURIComponent(serviceId)}/runs/development`, {
        method: 'POST', body: JSON.stringify(attempt),
      })
      setNotice(result.request?.state === 'verified'
        ? '기존 요청의 검증 완료 상태를 확인했습니다.'
        : '개발 배포 요청을 접수했습니다. 실제 배포 완료 여부는 아래 상태에서 확인하세요.')
      await refresh()
      retry.current = null
    } catch (cause) { setError(cause.message || '개발 배포 요청을 확인할 수 없습니다. 같은 요청으로 다시 확인하세요.') }
    finally { setBusy(false) }
  }

  return <section className="deploy-panel" aria-labelledby="development-run-heading">
    <h2 id="development-run-heading">개발 서버 배포</h2>
    {!eligible ? <p>개발 대상을 Kubernetes의 antsnest 네임스페이스로 등록해야 요청할 수 있습니다.</p> : <>
      <p>소스 저장소의 정확한 커밋을 지정합니다. 요청 접수는 빌드·배포 완료가 아닙니다.</p>
      {!executionConnected && <p className="deploy-warning" role="status">자동 실행기가 연결되지 않아 배포 요청을 받지 않습니다. 연결이 확인되면 버튼이 활성화됩니다.</p>}
      <form className="deploy-form" onSubmit={submit}>
        <label>소스 커밋 식별값
          <input value={sourceCommit} maxLength={40} spellCheck="false" autoComplete="off"
            placeholder="40자리 소문자 영숫자" onChange={event => {
              setSourceCommit(event.target.value.trim().toLowerCase()); retry.current = null
            }} />
        </label>
        <button type="submit" disabled={!executionConnected || busy || active || !COMMIT.test(sourceCommit)}>
          {active ? '기존 배포 진행 중' : busy ? '요청 확인 중…' : '개발 배포 요청'}
        </button>
      </form>
      {error && <p className="deploy-error" role="alert">{error}</p>}
      {notice && <p className="deploy-notice" role="status">{notice}</p>}
      <div className="deploy-section-heading"><h3>최근 요청</h3>
        <button type="button" className="deploy-new" onClick={() => refresh().catch(() => setError('개발 배포 이력을 읽을 수 없습니다.'))}>상태 새로고침</button></div>
      {requests.length ? <ol className="deploy-run-list">{requests.map(request => <li key={request.runId}>
        <strong>{STATE[request.state] || '상태 확인 필요'}</strong>
        <span>{request.sourceCommit?.slice(0, 12)} · {request.target}</span>
      </li>)}</ol> : <p>아직 개발 배포 요청이 없습니다.</p>}
    </>}
  </section>
}
